import { useMutation, useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

const CREATE_TRANSACTION_QUERY = gql`
  mutation insertOneTransfer($transfer: TransferInsertInput!) {
    insertOneTransfer(data: $transfer) {
      _id
    }
  }
`;

export function useCreateTransfer() {
  const [createTransfer, { loading, error, data }] = useMutation(
    CREATE_TRANSACTION_QUERY,
  );
  return {
    createTransfer,
    loading,
    error,
    tenant: data ? data.createTransfer : undefined,
  };
}

const UPDATE_TRANSACTION_QUERY = gql`
  mutation updateOneTransfer(
    $query: TransferQueryInput!
    $data: TransferUpdateInput!
  ) {
    updateOneTransfer(query: $query, set: $data) {
      _id
    }
  }
`;

export function useSaveTransfer() {
  const [saveTransfer, { loading, error, data }] = useMutation(
    UPDATE_TRANSACTION_QUERY,
  );

  return {
    saveTransfer,
    loading,
    error,
    transfer: data ? data.updateOneTransfer : undefined,
  };
}

const GET_TRANSACTION_QUERY = gql`
  query transfer($id: ObjectId!) {
    transfer(query: { _id: $id }) {
      _id
      firstName
      lastName
      email
      mobilePhone
      propertyAddress1
      propertyAddress2
      debitMethod
      propertyCity
      propertyState
      propertyZipcode
      purpose
      status
      amount
      fee
      expectedClosingDate_utc
      notes
      sellerName
      completedOn_utc
      clearedOn_utc
      purposeData
      updatedAt_utc
      referenceID
      creditTraceID
      disbursement
      client {
        firstName
        lastName
      }
      tenant {
        name
      }
      attachments {
        _id
        name
        filePath
        fileURL
      }
      transferType {
        _id
        name
        purpose
      }
      otherIndividuals
      destBankAccount {
        _id
      }
      sourceBankAccount {
        _id
        bankName
        description
        accountLastFourDigits
        bankDisplayName
        active
        institutionCode
        client {
          _id
        }
        updatedAt_utc
        createdAt_utc
      }
      office {
        _id
        title
        city
        state
      }
      realtor {
        _id
        firstName
        lastName
        email
      }
    }
  }
`;

export function useTransfer(id) {
  const { loading, error, refetch, data } = useQuery(GET_TRANSACTION_QUERY, {
    variables: { id },
    fetchPolicy: 'no-cache',
    skip: !id, // Skip the query if the ID is not present
  });
  const refetchTransfer = () => {
    if (id) {
      refetch();
    }
  };

  return {
    loadingSearchTransfer: loading,
    error,
    refetchTransfer,
    transfer: data ? data.transfer : undefined,
  };
}

const GET_TRANSACTIONS_QUERY = gql`
  query transfers($clientId: ObjectId!, $tenantId: ObjectId!) {
    transfers(
      query: { client: { _id: $clientId }, tenant: { _id: $tenantId } }
    ) {
      _id
      firstName
      middleName
      lastName
      otherIndividuals
      email
      mobilePhone
      propertyAddress1
      propertyAddress2
      propertyCity
      propertyState
      propertyZipcode
      amount
      purpose
      expectedClosingDate_utc
      sellerName
      notes
      status
      active
      disbursement
      transferType {
        _id
        name
        purpose
      }
      sourceBankAccount {
        _id
        bankName
        description
        accountLastFourDigits
        bankDisplayName
        active
        client {
          _id
        }
        institutionCode
        updatedAt_utc
        createdAt_utc
      }
      destBankAccount {
        _id
        bankName
        description

        institutionCode
      }
      office {
        _id
        title
      }
      realtor {
        firstName
        lastName
        _id
        email
      }
      updatedAt_utc
      createdAt_utc
    }
  }
`;

export function useAllTransfers(clientId, tenantId) {
  const { loading, error, data, refetch } = useQuery(GET_TRANSACTIONS_QUERY, {
    variables: {
      clientId: clientId,
      tenantId: tenantId,
    },
    fetchPolicy: 'no-cache',
    skip: !clientId?.length,
  });

  return {
    loadingSearchTransfer: loading,
    error,
    refetchTransfers: refetch,
    transfers: data ? data.allTransfers : undefined,
  };
}
